import { formatBps, formatDay, formatMonthYear } from '@bemor/web-kit';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { RiskDashboard } from '@/api/types';

/* BM-125 slice 3: the Risk dashboard's charts on the same recharts set as the finance report (BM-122): the kit's
   theme leaves --chart-1..4 empty in the apps, so each colour names its design-token fallback. Figures are the
   server's; nothing is summed in the browser. */

const C1 = 'var(--chart-1, var(--fg-1))';
const C2 = 'var(--chart-2, var(--signal-500))';
const C3 = 'var(--chart-3, var(--gray-400))';
const C4 = 'var(--chart-4, var(--gray-200))';
const DANGER = 'var(--danger-solid)';
const WARNING = 'var(--warning-solid)';

// self-review: axis labels in words ("Oct 30, 2026", "Oct 2026"), never an ISO fragment (UI rule 0.2)
const dayLabel = (d: string) => formatDay(d);
/** The heatmap's column head: the day of the month; the window's dates are in the section above. */
const dayOfMonth = (d: string) => String(Number(d.slice(8, 10)));
const pct = (v: number) => formatBps(v);

function Empty({ text }: { text: string }) {
    return <p className="text-fg-2 m-0 py-8 text-center text-sm">{text}</p>;
}

/** Monthly ratios with the card networks' warning and breach lines (VAMP and chargebacks share the lines). */
export function RatioTrendChart({
    rows,
    warningBps,
    breachBps,
}: {
    rows: readonly {
        period_start: string;
        chargeback_ratio_bps?: number | null;
        vamp_ratio_bps?: number | null;
        fraud_value_bps?: number | null;
    }[];
    warningBps: number;
    breachBps: number;
}) {
    if (rows.length === 0) return <Empty text="No monthly figures yet." />;
    const data = rows.map((r) => ({
        label: formatMonthYear(r.period_start.slice(0, 7)),
        chargebacks: r.chargeback_ratio_bps ?? null,
        vamp: r.vamp_ratio_bps ?? null,
        fraud: r.fraud_value_bps ?? null,
    }));
    return (
        <div className="h-64 w-full" data-testid="chart-ratio-trend">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                    <CartesianGrid
                        stroke="var(--chart-grid)"
                        vertical={false}
                    />
                    <XAxis dataKey="label" fontSize={12} />
                    <YAxis tickFormatter={pct} fontSize={12} width={56} />
                    <Tooltip formatter={(v) => pct(Number(v))} />
                    <Legend />
                    <ReferenceLine
                        y={warningBps}
                        stroke={WARNING}
                        strokeDasharray="4 4"
                        label={{
                            value: 'Warning',
                            fontSize: 11,
                            position: 'insideTopRight',
                        }}
                    />
                    <ReferenceLine
                        y={breachBps}
                        stroke={DANGER}
                        strokeDasharray="4 4"
                        label={{
                            value: 'Breach',
                            fontSize: 11,
                            position: 'insideTopRight',
                        }}
                    />
                    <Line
                        type="monotone"
                        dataKey="chargebacks"
                        name="Chargebacks"
                        stroke={C1}
                        dot={false}
                        connectNulls
                    />
                    <Line
                        type="monotone"
                        dataKey="vamp"
                        name="VAMP"
                        stroke={C2}
                        dot={false}
                        connectNulls
                    />
                    <Line
                        type="monotone"
                        dataKey="fraud"
                        name="Fraud by value"
                        stroke={C3}
                        dot={false}
                        connectNulls
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

/** Attempts per day by verdict. */
export function AttemptsChart({ rows }: { rows: RiskDashboard['attempts'] }) {
    if (rows.length === 0) return <Empty text="No checks in this window." />;
    return (
        <div className="h-64 w-full" data-testid="chart-attempts">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={rows.map((r) => ({ ...r, label: dayLabel(r.day) }))}
                >
                    <CartesianGrid
                        stroke="var(--chart-grid)"
                        vertical={false}
                    />
                    <XAxis dataKey="label" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} width={40} />
                    <Tooltip />
                    <Legend />
                    <Bar
                        dataKey="allowed"
                        name="Allowed"
                        stackId="a"
                        fill={C4}
                    />
                    <Bar
                        dataKey="three_ds"
                        name="3-D Secure"
                        stackId="a"
                        fill={C3}
                    />
                    <Bar
                        dataKey="reviewed"
                        name="Review"
                        stackId="a"
                        fill={C2}
                    />
                    <Bar
                        dataKey="declined"
                        name="Declined"
                        stackId="a"
                        fill={C1}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

const REASONS: Record<string, string> = {
    issuer: 'Issuer',
    risk: 'Risk rules',
    three_ds: '3-D Secure failed',
    review: 'Declined in review',
};

/** Declines by who declined. */
export function DeclinesChart({
    rows,
}: {
    rows: RiskDashboard['declines_by_reason'];
}) {
    if (rows.every((r) => r.count === 0))
        return <Empty text="No declines in this window." />;
    return (
        <div className="h-48 w-full" data-testid="chart-declines">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={rows.map((r) => ({
                        ...r,
                        label: REASONS[r.reason] ?? r.reason,
                    }))}
                    layout="vertical"
                >
                    <CartesianGrid
                        stroke="var(--chart-grid)"
                        horizontal={false}
                    />
                    <XAxis type="number" allowDecimals={false} fontSize={12} />
                    <YAxis
                        type="category"
                        dataKey="label"
                        width={130}
                        fontSize={12}
                    />
                    <Tooltip />
                    <Bar dataKey="count" name="Declines" fill={C1} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

const AGES: Record<string, string> = {
    lt_4h: 'Under 4 h',
    '4_24h': '4–24 h',
    '24_72h': '24–72 h',
    gt_72h: 'Over 72 h',
};

/** The open review queue by age. */
export function QueueChart({ queue }: { queue: RiskDashboard['queue'] }) {
    return (
        <div className="h-48 w-full" data-testid="chart-queue">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={queue.ageing.map((a) => ({
                        ...a,
                        label: AGES[a.bucket] ?? a.bucket,
                    }))}
                >
                    <CartesianGrid
                        stroke="var(--chart-grid)"
                        vertical={false}
                    />
                    <XAxis dataKey="label" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} width={40} />
                    <Tooltip />
                    <Bar dataKey="count" name="Open cases" fill={C2} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

/** The score histogram of the pre-authorization checks, with the profile's bands. */
export function ScoreChart({
    rows,
    bands,
}: {
    rows: RiskDashboard['score_histogram'];
    bands: RiskDashboard['bands'];
}) {
    const data = rows.map((r) => ({
        ...r,
        label: r.from >= 100 ? '100+' : String(r.from),
    }));
    const at = (score: number | null | undefined) =>
        score == null
            ? null
            : score >= 100
              ? '100+'
              : String(Math.floor(score / 10) * 10);
    return (
        <div className="h-56 w-full" data-testid="chart-scores">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data}>
                    <CartesianGrid
                        stroke="var(--chart-grid)"
                        vertical={false}
                    />
                    <XAxis dataKey="label" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} width={40} />
                    <Tooltip />
                    <Bar dataKey="count" name="Checks" fill={C1} />
                    {(
                        [
                            ['three_ds', '3DS', C3],
                            ['review', 'Review', WARNING],
                            ['decline', 'Decline', DANGER],
                        ] as const
                    ).map(([key, label, color]) => {
                        const x = at(bands[key]);
                        return x === null ? null : (
                            <ReferenceLine
                                key={key}
                                x={x}
                                stroke={color}
                                strokeDasharray="4 4"
                                label={{
                                    value: label,
                                    fontSize: 11,
                                    position: 'top',
                                }}
                            />
                        );
                    })}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

/** The highest declines on one BIN in one hour, per day. */
export function EnumerationChart({
    rows,
}: {
    rows: RiskDashboard['enumeration'];
}) {
    if (rows.length === 0)
        return <Empty text="No daily figures in this window." />;
    return (
        <div className="h-48 w-full" data-testid="chart-enumeration">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={rows.map((r) => ({ ...r, label: dayLabel(r.day) }))}
                >
                    <CartesianGrid
                        stroke="var(--chart-grid)"
                        vertical={false}
                    />
                    <XAxis dataKey="label" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} width={40} />
                    <Tooltip />
                    <Line
                        type="monotone"
                        dataKey="max_declines_per_bin_hour"
                        name="Declines per BIN per hour"
                        stroke={C2}
                        dot={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

/** Rule hits per day as a heatmap: rows are rules, columns days, the shade the share of the busiest cell. */
export function RuleHeatmap({ rows }: { rows: RiskDashboard['rule_hits'] }) {
    if (rows.length === 0) return <Empty text="No rule hits in this window." />;
    const rules = [...new Set(rows.map((r) => r.rule))];
    const days = [...new Set(rows.map((r) => r.day))].sort();
    const max = Math.max(...rows.map((r) => r.hits));
    const hits = new Map(rows.map((r) => [`${r.rule}|${r.day}`, r.hits]));
    return (
        <div className="overflow-x-auto" data-testid="chart-rule-heatmap">
            <Table className="w-auto text-xs">
                <TableHeader>
                    <TableRow>
                        <TableHead>Rule</TableHead>
                        {days.map((d) => (
                            <TableHead key={d} className="px-0.5 font-normal">
                                {dayOfMonth(d)}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rules.map((rule) => (
                        <TableRow key={rule}>
                            <TableCell className="font-mono whitespace-nowrap">
                                {rule}
                            </TableCell>
                            {days.map((d) => {
                                const n = hits.get(`${rule}|${d}`) ?? 0;
                                return (
                                    <TableCell key={d} className="p-0.5">
                                        <span
                                            aria-label={`${rule} ${formatDay(d)}: ${n} hits`}
                                            className="block h-5 min-w-5 rounded-sm"
                                            style={{
                                                background:
                                                    n === 0
                                                        ? 'var(--gray-100, transparent)'
                                                        : C1,
                                                opacity:
                                                    n === 0
                                                        ? 1
                                                        : 0.2 + (0.8 * n) / max,
                                            }}
                                        />
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
